/* ============================================================================
 * TeslaHubs — Variant B: Cart Savings Continuity
 * Scope: /cart only.
 *
 * Mobile:
 * - current price stays theme-native;
 * - compare-at + SAVE stay together on one line;
 * - injected pair uses native size whenever possible;
 * - JS measures real free space up to quantity / Remove;
 * - only injected elements scale when genuinely necessary;
 * - injected UI can never force native cart controls outside the viewport.
 * ==========================================================================*/
(function () {
  'use strict';

  if (!/(^|\/)cart\/?$/.test(window.location.pathname)) {
    return;
  }

  if (window.__igCartSavingsContinuity) {
    return;
  }

  window.__igCartSavingsContinuity = true;

  var MARK = 'data-ig-savings';
  var FIT_MARK = 'data-ig-savings-fit';

  var SAVE_LABEL = 'Save ';
  var CART_SAVE_LABEL = 'You save ';

  var renderToken = 0;
  var fitRaf = 0;

  var productCache = Object.create(null);

  /* ==========================================================================
   * Helpers
   * ======================================================================== */

  function rootUrl() {
    var root = '/';

    try {
      if (
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
      ) {
        root = String(window.Shopify.routes.root);
      }
    } catch (e) {
      root = '/';
    }

    return root.charAt(root.length - 1) === '/'
      ? root
      : root + '/';
  }

  function formatMoney(cents, forceWithCurrency) {
    try {
      if (
        !window.theme ||
        !window.theme.Currency ||
        typeof window.theme.Currency.formatMoney !== 'function'
      ) {
        return null;
      }

      var settings = window.theme.settings || {};

      var format = forceWithCurrency
        ? settings.moneyWithCurrencyFormat
        : (
            settings.currencyCodeEnabled
              ? settings.moneyWithCurrencyFormat
              : settings.moneyFormat
          );

      if (!format) {
        return null;
      }

      var output = window.theme.Currency.formatMoney(
        Number(cents),
        format
      );

      return typeof output === 'string' && output.length
        ? output
        : null;
    } catch (e) {
      return null;
    }
  }

  function stripTrailingZeroDecimals(value) {
    if (typeof value !== 'string') {
      return value;
    }

    return value.replace(
      /([.,])00(?=(?:\s*[^\d]*)?$)/,
      ''
    );
  }

  function formatSavingsMoney(cents, forceWithCurrency) {
    var formatted = formatMoney(
      cents,
      forceWithCurrency
    );

    if (!formatted) {
      return null;
    }

    return stripTrailingZeroDecimals(
      formatted
    );
  }

  function floorToUnit(cents) {
    return Math.floor(
      Number(cents) / 100
    ) * 100;
  }

  function getJSON(url) {
    return fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    }).then(function (response) {
      if (!response.ok) {
        throw new Error(
          'HTTP ' + response.status
        );
      }

      return response.json();
    });
  }

  function fetchCart() {
    return getJSON(
      rootUrl() + 'cart.js'
    ).catch(function () {
      return null;
    });
  }

  function fetchCompareAtMap(handle) {
    if (productCache[handle]) {
      return productCache[handle];
    }

    var pending = getJSON(
      rootUrl() +
      'products/' +
      encodeURIComponent(handle) +
      '.js'
    )
      .then(function (json) {
        var map = Object.create(null);

        var variants =
          json && json.variants
            ? json.variants
            : [];

        for (var i = 0; i < variants.length; i++) {
          var variant = variants[i];

          if (
            !variant ||
            variant.id === undefined ||
            variant.id === null
          ) {
            continue;
          }

          map[String(variant.id)] =
            Number(variant.compare_at_price) || 0;
        }

        return map;
      })
      .catch(function () {
        delete productCache[handle];
        return null;
      });

    productCache[handle] = pending;

    return pending;
  }

  function resetFitStyles() {
    var pairs = document.querySelectorAll(
      '.cart [' + MARK + '="pair"]'
    );

    for (var i = 0; i < pairs.length; i++) {
      pairs[i].style.removeProperty('left');
      pairs[i].style.removeProperty('visibility');
      pairs[i].style.removeProperty('--ig-savings-scale');
    }

    var fitted = document.querySelectorAll(
      '[' + FIT_MARK + ']'
    );

    for (var j = 0; j < fitted.length; j++) {
      fitted[j].style.removeProperty('min-height');
      fitted[j].removeAttribute(FIT_MARK);
    }
  }

  function cleanup() {
    resetFitStyles();

    var injected =
      document.querySelectorAll(
        '[' + MARK + ']'
      );

    for (var i = 0; i < injected.length; i++) {
      if (injected[i].parentNode) {
        injected[i].parentNode.removeChild(
          injected[i]
        );
      }
    }
  }

  function isExcludedItem(item) {
    if (!item) {
      return true;
    }

    if (item.selling_plan_allocation != null) {
      return true;
    }

    if (
      Number(item.original_price) !==
      Number(item.final_price)
    ) {
      return true;
    }

    var allocations =
      item.line_level_discount_allocations;

    if (
      allocations &&
      allocations.length > 0
    ) {
      return true;
    }

    return false;
  }

  function buildRowIndex() {
    var rows =
      document.querySelectorAll(
        'tr.cart-item'
      );

    var byKey = Object.create(null);
    var byVariant = Object.create(null);
    var variantCounts = Object.create(null);

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];

      var input =
        row.querySelector(
          'input.quantity__input[data-quantity-variant-id]'
        ) ||
        row.querySelector(
          'input.quantity__input'
        );

      var key = input
        ? input.getAttribute('data-index')
        : null;

      if (
        !key &&
        row.id &&
        row.id.indexOf('CartItem-') === 0
      ) {
        key = row.id.slice(
          'CartItem-'.length
        );
      }

      if (key) {
        byKey[key] = row;
      }

      var variantId = input
        ? input.getAttribute(
            'data-quantity-variant-id'
          )
        : null;

      if (variantId) {
        variantCounts[variantId] =
          (variantCounts[variantId] || 0) + 1;

        byVariant[variantId] = row;
      }
    }

    for (var id in variantCounts) {
      if (variantCounts[id] > 1) {
        delete byVariant[id];
      }
    }

    return {
      byKey: byKey,
      byVariant: byVariant,
      count: rows.length
    };
  }

  /* ==========================================================================
   * Adaptive mobile fitting
   * ======================================================================== */

  function getSafeRightBoundary(row, currentRect) {
    if (!row) {
      return null;
    }

    var candidates = [];

    /*
     * Measure actual native controls rather than the beginning
     * of their table cell. Unused cell padding remains available.
     */
    var selectors = [
      '.cart-quantity',
      'quantity-input',
      'a[is="cart-remove-button"]',
      'input.quantity__input'
    ];

    for (var i = 0; i < selectors.length; i++) {
      var node =
        row.querySelector(
          selectors[i]
        );

      if (!node) {
        continue;
      }

      var rect =
        node.getBoundingClientRect();

      if (
        rect.width > 0 &&
        rect.left > currentRect.right
      ) {
        candidates.push(
          rect.left
        );
      }
    }

    if (candidates.length) {
      return Math.min.apply(
        Math,
        candidates
      );
    }

    /*
     * Conservative fallback.
     */
    var quantityCell =
      row.querySelector(
        'td.cart-item__quantity'
      );

    if (quantityCell) {
      var cellRect =
        quantityCell.getBoundingClientRect();

      if (
        cellRect.left >
        currentRect.right
      ) {
        return cellRect.left;
      }
    }

    return null;
  }

  function fitInjectedPairs() {
    fitRaf = 0;

    /*
     * IMPORTANT:
     * If viewport is no longer mobile, remove every inline
     * value created by the mobile fitter.
     */
    if (
      !window.matchMedia(
        '(max-width: 767px)'
      ).matches
    ) {
      resetFitStyles();
      return;
    }

    var pairs =
      document.querySelectorAll(
        '.cart [' +
        MARK +
        '="pair"]'
      );

    for (var i = 0; i < pairs.length; i++) {
      var pair = pairs[i];

      var priceBox =
        pair.parentNode;

      if (!priceBox) {
        continue;
      }

      var currentPrice =
        priceBox.querySelector(
          '.price__regular'
        );

      if (!currentPrice) {
        pair.style.visibility =
          'hidden';

        continue;
      }

      var row =
        pair.closest(
          'tr.cart-item'
        );

      var priceRect =
        priceBox.getBoundingClientRect();

      var currentRect =
        currentPrice.getBoundingClientRect();

      /*
       * Visual breathing room after current price.
       */
      var startGap = 5;

      /*
       * Safety buffer before quantity / Remove.
       */
      var endGap = 6;

      var left =
        currentRect.right -
        priceRect.left +
        startGap;

      pair.style.left =
        left + 'px';

      /*
       * Always measure at native scale first.
       */
      pair.style.setProperty(
        '--ig-savings-scale',
        '1'
      );

      var naturalWidth =
        pair.scrollWidth;

      var safeRight =
        getSafeRightBoundary(
          row,
          currentRect
        );

      var availableWidth;

      if (safeRight !== null) {
        availableWidth =
          safeRight -
          currentRect.right -
          startGap -
          endGap;
      } else {
        /*
         * Conservative viewport fallback.
         */
        availableWidth =
          window.innerWidth -
          currentRect.right -
          12;
      }

      if (
        naturalWidth <= 0 ||
        availableWidth <= 0
      ) {
        pair.style.visibility =
          'hidden';

        continue;
      }

      /*
       * Native scale whenever it fits.
       *
       * Otherwise scale EXACTLY to available width.
       *
       * There is deliberately NO artificial minimum such as 0.55:
       * a minimum could make the injected pair wider than the
       * measured safe area and recreate the overlap bug.
       */
      var scale =
        Math.min(
          1,
          availableWidth /
          naturalWidth
        );

      /*
       * Numerical guard only.
       */
      scale =
        Math.max(
          0.01,
          scale
        );

      pair.style.setProperty(
        '--ig-savings-scale',
        String(scale)
      );

      pair.style.visibility =
        'visible';

      /*
       * Preserve vertical room for the transformed pair.
       */
      var visualPairHeight =
        pair.offsetHeight *
        scale;

      var requiredHeight =
        Math.max(
          currentPrice.offsetHeight,
          visualPairHeight
        );

      priceBox.style.minHeight =
        Math.ceil(
          requiredHeight
        ) + 'px';

      priceBox.setAttribute(
        FIT_MARK,
        '1'
      );
    }
  }

  function scheduleFit() {
    if (fitRaf) {
      cancelAnimationFrame(
        fitRaf
      );
    }

    fitRaf =
      requestAnimationFrame(
        fitInjectedPairs
      );
  }

  /* ==========================================================================
   * Paint
   * ======================================================================== */

  function paint(
    cart,
    compareMapsByHandle
  ) {
    var index =
      buildRowIndex();

    if (!index.count) {
      cleanup();
      return;
    }

    cleanup();

    var items =
      cart.items || [];

    var aggregate = 0;
    var displayedAny = false;
    var suppressAggregate = false;

    var cartDiscounts =
      cart.cart_level_discount_applications;

    if (
      cartDiscounts &&
      cartDiscounts.length > 0
    ) {
      suppressAggregate = true;
    }

    for (var a = 0; a < items.length; a++) {
      if (
        isExcludedItem(
          items[a]
        )
      ) {
        suppressAggregate = true;
        break;
      }
    }

    for (var i = 0; i < items.length; i++) {
      var item = items[i];

      if (
        !item ||
        !item.handle
      ) {
        continue;
      }

      if (
        isExcludedItem(item)
      ) {
        continue;
      }

      var map =
        compareMapsByHandle[
          item.handle
        ];

      if (!map) {
        continue;
      }

      var compareAt =
        Number(
          map[
            String(
              item.variant_id
            )
          ]
        ) || 0;

      /*
       * Current price always comes from cart state.
       */
      var current =
        Number(
          item.final_price
        );

      if (
        !isFinite(current) ||
        compareAt <= 0 ||
        compareAt <= current
      ) {
        continue;
      }

      var displayedSaving =
        floorToUnit(
          compareAt -
          current
        );

      if (
        displayedSaving <= 0
      ) {
        continue;
      }

      var row =
        index.byKey[
          item.key
        ] ||
        index.byVariant[
          String(
            item.variant_id
          )
        ];

      if (!row) {
        continue;
      }

      var priceBox =
        row.querySelector(
          '.cart-item__details .price'
        );

      if (!priceBox) {
        continue;
      }

      var compareText =
        formatMoney(
          compareAt,
          false
        );

      var savingText =
        formatSavingsMoney(
          displayedSaving,
          false
        );

      if (
        !compareText ||
        !savingText
      ) {
        continue;
      }

      var pair =
        document.createElement(
          'span'
        );

      pair.className =
        'ig-cart-savings-pair';

      pair.setAttribute(
        MARK,
        'pair'
      );

      var compareEl =
        document.createElement(
          'span'
        );

      compareEl.className =
        'price__sale';

      compareEl.innerHTML =
        compareText;

      var pillEl =
        document.createElement(
          'span'
        );

      pillEl.className =
        'price__savings';

      pillEl.innerHTML =
        SAVE_LABEL +
        savingText;

      pair.appendChild(
        compareEl
      );

      pair.appendChild(
        pillEl
      );

      var anchor =
        priceBox.querySelector(
          '.price__regular'
        );

      if (
        anchor &&
        anchor.parentNode ===
          priceBox
      ) {
        priceBox.insertBefore(
          pair,
          anchor.nextSibling
        );
      } else {
        priceBox.appendChild(
          pair
        );
      }

      var quantity =
        parseInt(
          item.quantity,
          10
        );

      if (
        !isFinite(quantity) ||
        quantity < 1
      ) {
        quantity = 1;
      }

      aggregate +=
        displayedSaving *
        quantity;

      displayedAny = true;
    }

    if (displayedAny) {
      scheduleFit();
    }

    if (
      !displayedAny ||
      aggregate <= 0
    ) {
      return;
    }

    if (suppressAggregate) {
      return;
    }

    var totals =
      document.querySelector(
        '.cart__sidebar .totals'
      );

    if (
      !totals ||
      !totals.parentNode
    ) {
      return;
    }

    var aggregateText =
      formatSavingsMoney(
        aggregate,
        true
      );

    if (!aggregateText) {
      return;
    }

    var subtotalSavings =
      document.createElement(
        'div'
      );

    subtotalSavings.className =
      'ig-cart-savings';

    subtotalSavings.setAttribute(
      MARK,
      'subtotal'
    );

    subtotalSavings.innerHTML =
      CART_SAVE_LABEL +
      aggregateText;

    totals.parentNode.insertBefore(
      subtotalSavings,
      totals.nextSibling
    );
  }

  /* ==========================================================================
   * Render
   * ======================================================================== */

  function render(
    cartFromEvent
  ) {
    var token =
      ++renderToken;

    if (
      !document.querySelector(
        'main-cart'
      )
    ) {
      cleanup();
      return;
    }

    var cartPromise =
      cartFromEvent &&
      cartFromEvent.items
        ? Promise.resolve(
            cartFromEvent
          )
        : fetchCart();

    cartPromise
      .then(function (cart) {
        if (
          token !== renderToken
        ) {
          return;
        }

        if (
          !cart ||
          !cart.items ||
          !cart.items.length
        ) {
          cleanup();
          return;
        }

        var handles = [];
        var seen =
          Object.create(null);

        for (
          var i = 0;
          i < cart.items.length;
          i++
        ) {
          var item =
            cart.items[i];

          if (
            !item ||
            !item.handle
          ) {
            continue;
          }

          if (
            isExcludedItem(
              item
            )
          ) {
            continue;
          }

          if (
            seen[item.handle]
          ) {
            continue;
          }

          seen[item.handle] =
            true;

          handles.push(
            item.handle
          );
        }

        if (!handles.length) {
          cleanup();
          return;
        }

        return Promise.all(
          handles.map(
            fetchCompareAtMap
          )
        ).then(function (maps) {
          if (
            token !== renderToken
          ) {
            return;
          }

          var byHandle =
            Object.create(null);

          for (
            var j = 0;
            j < handles.length;
            j++
          ) {
            if (maps[j]) {
              byHandle[
                handles[j]
              ] = maps[j];
            }
          }

          paint(
            cart,
            byHandle
          );
        });
      })
      .catch(function () {
        /*
         * Fail-safe:
         * leave cart unchanged.
         */
      });
  }

  /* ==========================================================================
   * Lifecycle
   * ======================================================================== */

  document.addEventListener(
    'cart:updated',
    function (event) {
      var cart = null;

      try {
        cart =
          event &&
          event.detail &&
          event.detail.cart
            ? event.detail.cart
            : null;
      } catch (e) {
        cart = null;
      }

      render(cart);
    }
  );

  window.addEventListener(
    'resize',
    scheduleFit
  );

  if (window.visualViewport) {
    window.visualViewport
      .addEventListener(
        'resize',
        scheduleFit
      );
  }

  function boot() {
    render(null);

    try {
      if (
        document.fonts &&
        document.fonts.ready
      ) {
        document.fonts.ready.then(
          scheduleFit
        );
      }
    } catch (e) {}
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot
    );
  } else {
    boot();
  }
})();